import { Fragment } from 'react'
import { Popover, Transition } from '@headlessui/react'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  CaretDoubleRight,
  CaretDown,
  CaretRight,
  CheckCircle,
  Circle,
  GridFour,
  NumberCircleOne,
  NumberCircleThree,
  NumberCircleTwo,
} from '@phosphor-icons/react'

import { useAppContext } from '@/main/app/context'
import { useAppSelector } from '@/main/app/hooks/storeHooks'
import { selectListById } from '@/main/features/List/listSlice'
import { useMediaQuery } from '@/main/app/hooks/useMediaQuery'
import { ListMenu } from '@/main/features/List/components/ListMenu/ListMenu'

const inactiveCrumb = 'twc-text-slate-600 twc-font-medium dark:twc-text-slate-300'
const activeCrumb = 'twc-text-indigo-700 twc-font-bold dark:twc-text-indigo-200'
const linkCrumb =
  'twc-inline-flex twc-text-inherit twc-group twc-gap-1 twc-items-center hover:twc-text-brand dark:hover:twc-text-orange-300'
const itemCrumb =
  'twc-inline-flex twc-shrink-0 twc-items-center twc-text-sm sm:twc-tracking-tight md:twc-tracking-normal sm:twc-text-xm md:twc-text-base'
const dividerCrumb = `${itemCrumb} twc-hidden sm:twc-inline-flex`

function CrumbsList({ onNavigate }: { onNavigate?: () => void }) {
  const {
    formPaths: [step1, step2, step3],
  } = useAppContext()

  const { id } = useParams()
  const location = useLocation()
  const list = useAppSelector((state) => selectListById(state, id ?? 0))
  const form = list?.form

  const indexIsActive = location.pathname === '/'
  const mainIsActive = location.pathname === `/${id}`
  const step1Active = location.pathname.indexOf(step1) > -1
  const step2Active = location.pathname.indexOf(step2) > -1
  const step3Active = location.pathname.indexOf(step3) > -1
  const isSuccess = location.pathname.indexOf('success') > -1

  const step1Complete = form?.profile?.status === 'saved' && form?.billing?.status === 'saved'
  const step2Complete = form?.dates?.status === 'saved' && form?.shipping?.status === 'saved'

  const handleNavigate = () => {
    if (onNavigate !== undefined) {
      onNavigate()
    }
  }

  return (
    <ul className="twc-flex twc-flex-col sm:twc-flex-row twc-items-end sm:twc-items-center twc-gap-4 sm:twc-gap-1 md:twc-gap-2 sm:twc-h-8 md:twc-h-10 xl:twc-gap-4">
      <li className={itemCrumb}>
        <Link
          onClick={handleNavigate}
          to={`/${id ?? ''}`}
          className={`${
            mainIsActive
              ? 'dark:twc-text-white twc-font-bold'
              : 'dark:twc-text-slate-300 twc-font-medium'
          } twc-text-inherit twc-inline-flex twc-gap-1 twc-items-center twc-shrink-0 hover:twc-text-brand dark:hover:twc-text-orange-300`}
        >
          <Circle
            size={20}
            weight={mainIsActive ? 'fill' : 'duotone'}
            className="twc-text-indigo-500"
          />
          <span>{list?.name ?? 'Cart'}</span>
        </Link>
      </li>

      <li className={dividerCrumb} aria-hidden="true">
        <CaretDoubleRight size={12} className="twc-text-slate-400" weight="bold" />
      </li>

      <li className={`${itemCrumb} ${step1Active ? activeCrumb : inactiveCrumb}`}>
        <Link
          onClick={handleNavigate}
          className={`${linkCrumb} ${
            indexIsActive
              ? 'twc-pointer-events-none twc-text-slate-400 hover:twc-text-slate-400 dark:twc-text-slate-400 dark:hover:twc-text-slate-400'
              : 'twc-pointer-events-auto'
          }`}
          to={`/${id}/form/${step1}`}
        >
          {step1Complete && !step1Active ? (
            <CheckCircle size={20} weight="bold" className="twc-text-indigo-500" />
          ) : (
            <NumberCircleOne size={20} weight={step1Active ? 'bold' : 'light'} />
          )}
          <span>Contact &amp; Project</span>
        </Link>
      </li>

      <li className={dividerCrumb} aria-hidden="true">
        <CaretRight size={12} className="twc-text-slate-400" weight="bold" />
      </li>

      <li className={`${itemCrumb} ${step2Active ? activeCrumb : inactiveCrumb}`}>
        <Link
          onClick={handleNavigate}
          className={`${linkCrumb} ${
            !step1Complete
              ? 'twc-pointer-events-none twc-text-slate-400 hover:twc-text-slate-400 dark:twc-text-slate-400 dark:hover:twc-text-slate-400'
              : 'twc-pointer-events-auto'
          } ${
            step2Active
              ? 'twc-border-t-indigo-800'
              : step3Active || isSuccess
              ? 'twc-border-t-indigo-500'
              : !step1Complete
              ? 'twc-border-t-slate-200'
              : 'twc-border-t-slate-300'
          }`}
          to={`/${id}/form/${step2}`}
        >
          {step2Complete && !step1Active && !step2Active ? (
            <CheckCircle size={20} weight="bold" className="twc-text-indigo-500" />
          ) : (
            <NumberCircleTwo size={20} weight={step2Active ? 'bold' : 'light'} />
          )}
          Dates &amp; Pickup/Delivery
        </Link>
      </li>

      <li className={dividerCrumb} aria-hidden="true">
        <CaretRight size={12} className="twc-text-slate-400" weight="bold" />
      </li>

      <li className={`${itemCrumb} ${step3Active ? activeCrumb : inactiveCrumb}`}>
        <Link
          onClick={handleNavigate}
          className={`${linkCrumb} ${
            !step1Complete || !step2Complete
              ? 'twc-pointer-events-none twc-text-slate-400 hover:twc-text-slate-400 dark:twc-text-slate-400 dark:hover:twc-text-slate-400'
              : 'twc-pointer-events-auto'
          } ${
            step3Active
              ? 'twc-border-t-indigo-800'
              : isSuccess
              ? 'twc-border-t-indigo-500'
              : !step1Complete || !step2Complete
              ? 'twc-border-t-slate-200'
              : 'twc-border-t-slate-300'
          }`}
          to={`/${id}/form/${step3}`}
        >
          {isSuccess ? (
            <CheckCircle size={20} weight="fill" className="twc-text-indigo-500" />
          ) : (
            <NumberCircleThree size={20} weight={step3Active ? 'bold' : 'light'} />
          )}

          <span>Review &amp; Submit</span>
        </Link>
      </li>
    </ul>
  )
}

export function MainBreadcrumbs() {
  const matches = useMediaQuery('(min-width: 640px)')
  const { pathname } = useLocation()
  const indexIsActive = pathname === '/'

  return (
    <div className="menu twc-flex twc-items-center sm:twc-items-stretch sm:twc-flex-col twc-gap-2 sm:twc-gap-4">
      <div className="twc-flex twc-justify-between twc-grow twc-items-center">
        <Link
          to=""
          className={`twc-inline-flex twc-text-sm md:twc-text-base twc-gap-1 twc-items-center ${
            indexIsActive
              ? 'twc-text-slate-300 dark:twc-text-slate-600'
              : 'twc-text-indigo-600 dark:twc-text-indigo-200 twc-underline'
          } hover:twc-text-brand hover:twc-no-underline dark:hover:twc-text-orange-300`}
        >
          <GridFour size={20} weight="duotone" /> View All Projects
        </Link>

        <ListMenu align="right" />
      </div>

      <nav className="dark:twc-border-slate-800 twc-items-center">
        {matches ? (
          <div className="twc-py-1 twc-flex-shrink-0 sm:twc-border-y twc-border-y-slate-200 twc-flex twc-items-center dark:twc-border-y-slate-800">
            <CrumbsList />
          </div>
        ) : (
          <Popover className="twc-relative twc-max-w-sm">
            <Popover.Button className="twc-inline-flex twc-items-center twc-rounded twc-ml-auto twc-py-1 twc-px-2 twc-bg-sky-200 twc-text-sm dark:twc-bg-cyan-700 dark:hover:twc-bg-primary hover:twc-bg-sky-100 dark:hover:twc-text-white motion-safe:twc-transition-colors">
              Go to <CaretDown />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="twc-transition twc-ease-out twc-duration-200"
              enterFrom="twc-opacity-0 twc-translate-y-1"
              enterTo="twc-opacity-100 twc-translate-y-0"
              leave="twc-transition twc-ease-in twc-duration-150"
              leaveFrom="twc-opacity-100 twc-translate-y-0"
              leaveTo="twc-opacity-0 twc-translate-y-1"
            >
              <Popover.Panel className="twc-absolute twc-z-50 twc-right-0 twc-mt-2">
                {({ close }) => (
                  <div className="twc-bg-slate-200 twc-w-max dark:twc-bg-zinc-800 twc-rounded twc-p-3 twc-shadow-lg">
                    <CrumbsList onNavigate={close} />
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </Popover>
        )}
      </nav>
    </div>
  )
}
